import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDbHeaders } from "../../dataManagement/AnimoApi.js";
import { getSessionId, setSessionId } from "../../utils/sessionTracking/sessionStore.js";
import { X_SESSION_HEADER, isSessionHeaderEnabled } from "../../utils/sessionTracking/setupSessionInterceptor.js";
import { recordDebug } from "../../utils/sessionTracking/debugLog.js";
import { stampEvent } from "../../utils/sessionTracking/tabId.js";
import { isComponentType, namedComponentId } from "../../utils/sessionTracking/componentId.js";
import { createWorkflowTracker, isAreaType, isFinishWord } from "../../utils/sessionTracking/workflows.js";
import { watchPanels } from "../../utils/sessionTracking/panelWatcher.js";
import { watchClicks, getLastClick } from "../../utils/sessionTracking/clickWatcher.js";
import { watchIdle } from "../../utils/sessionTracking/idleWatcher.js";
import { watchResults } from "../../utils/sessionTracking/resultWatcher.js";
import { watchHighchartsWhenLoaded } from "../../utils/sessionTracking/highchartsWatcher.js";
import { subscribe, track } from "../../utils/sessionTracking/track.js";
import { describeRoute } from "../../utils/sessionTracking/describeRoute.js";
import { FE_VERSION } from "../../version";

export const TrackingContext = createContext(null);

const STORAGE_KEY = "trackLocal";
const PAGE_STORAGE_KEY = "trackPage";
const FLUSH_INTERVAL_MS = 60000;
const MAX_SEND_ATTEMPTS = 5;
const CLICK_TO_PANEL_MS = 1500;
const REQUEST_TIMEOUT_MS = 20000;
const FLUSH_STALE_MS = 30000;
const TIMER_LATE_FACTOR = 1.05;
const MIN_VISIT_MS = 500;
const UNTRACKED_PAGES = ["/auth", "/admin/sessionReview"];

const IP_LOOKUP_URL = "https://api.ipify.org?format=json";

const toPayload = ({ _attempts, _dirty, ...page }) => page;

const isDirty = (page) => page._dirty !== false;

const bumpAttempts = (bucket, sent) =>
  Object.keys(bucket).reduce((acc, key) => {
    const page = bucket[key];
    if (!sent[key]) {
      acc[key] = page;
      return acc;
    }
    const attempts = (page._attempts || 0) + 1;
    if (attempts < MAX_SEND_ATTEMPTS) acc[key] = { ...page, _attempts: attempts };
    return acc;
  }, {});

const emptySession = () => ({
  userSession: {
    Id: "",
    IP: "",
    userId: "",
    deviceType: "",
    frontend_version: "",
    tokenTail: "",
    start_time: "",
    end_time: "",
  },
  pageSession: { new: {}, old: {} },
});

const emptyRoute = () => ({ page: "", key: "", ids: "" });

const now = () => new Date().toISOString();

const generateId = (userId) => `${userId}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

const getTimeZone = () => {
  let name = "";
  try {
    name = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    name = "";
  }
  return { time_zone: name, utc_offset_minutes: -new Date().getTimezoneOffset() };
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Mac/i.test(userAgent)) return "Mac";
  return "Unknown";
};

const fetchIpAddress = async () => {
  try {
    const response = await fetch(IP_LOOKUP_URL);
    const data = await response.json();
    return data.ip;
  } catch {
    return "Unknown IP";
  }
};

const readJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const tokenTail = (token) => (token ? String(token).slice(-8) : "");

const readStoredToken = () => {
  try {
    return localStorage.getItem("token");
  } catch {
    return null;
  }
};

const loadStoredUserSession = () => {
  const stored = readJson(localStorage, STORAGE_KEY);
  const userSession = stored && stored.userSession ? stored.userSession : null;
  const token = readStoredToken();
  if (!userSession || !token || userSession.tokenTail !== tokenTail(token)) return emptySession().userSession;
  return userSession;
};

const loadStoredSession = () => {
  const userSession = loadStoredUserSession();
  const page = readJson(sessionStorage, PAGE_STORAGE_KEY);
  const pageSession = page && page.new && page.old ? page : emptySession().pageSession;
  setSessionId(userSession.Id);
  return { userSession, pageSession };
};

const persistSession = (session) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userSession: session.userSession }));
    sessionStorage.setItem(PAGE_STORAGE_KEY, JSON.stringify(session.pageSession));
  } catch {
    return;
  }
};

const sendKeepalive = (url, method, body, headers) => {
  const sessionId = isSessionHeaderEnabled() ? getSessionId() : null;
  fetch(url, {
    method,
    keepalive: true,
    headers: { ...headers, ...(sessionId ? { [X_SESSION_HEADER]: sessionId } : {}) },
    body: JSON.stringify(body),
  }).catch(() => {});
};

const keyByPageSessionKey = (response) => {
  if (!response) return {};
  if (Array.isArray(response)) {
    return response.reduce((acc, item) => {
      if (item && item.page_session_key) acc[item.page_session_key] = item;
      return acc;
    }, {});
  }
  return typeof response === "object" ? response : {};
};

const closePage = (page, endTime) => (page.end_time ? page : { ...page, end_time: endTime, active: false, _dirty: true });

const endAllPageSessions = (session) => {
  const endTime = now();
  const closeBucket = (bucket) =>
    Object.keys(bucket).reduce((acc, key) => {
      acc[key] = closePage(bucket[key], endTime);
      return acc;
    }, {});
  return {
    ...session,
    pageSession: {
      new: closeBucket(session.pageSession.new),
      old: closeBucket(session.pageSession.old),
    },
  };
};

const withTimeout = (headers) => ({ ...headers, timeout: REQUEST_TIMEOUT_MS });

const isUntracked = (page) => UNTRACKED_PAGES.some((prefix) => page === prefix || page.startsWith(prefix + "/"));

const withEvent = (session, key, event) => {
  const bucket = session.pageSession.new[key] ? "new" : session.pageSession.old[key] ? "old" : null;
  if (!bucket) return session;
  const page = session.pageSession[bucket][key];
  const details = page.details || {};
  const changed = { ...page, _dirty: true, details: { ...details, events: [...(details.events || []), stampEvent(event)] } };
  return { ...session, pageSession: { ...session.pageSession, [bucket]: { ...session.pageSession[bucket], [key]: changed } } };
};

const isPassingThrough = (page) => {
  if (!page || !page.end_time) return false;
  const events = (page.details && page.details.events) || [];
  return !events.length && new Date(page.end_time) - new Date(page.start_time) < MIN_VISIT_MS;
};

const appendTo = (page, event) => {
  const details = page.details || {};
  return { ...page, details: { ...details, events: [...(details.events || []), stampEvent(event)] } };
};

export const TrackingProvider = ({ children }) => {
  const dbUrl = useSelector((state) => state.url);
  const token = useSelector((state) => state.token);
  const reduxUserId = useSelector((state) => state.userId);
  const dbHeaders = useMemo(() => getDbHeaders(token), [token]);
  const location = useLocation();

  const [session, setSession] = useState(loadStoredSession);
  const sessionRef = useRef(session);
  const previousTokenRef = useRef(token);
  const flushingRef = useRef(0);
  const tickRef = useRef(() => {});
  const beatRef = useRef({ id: null, last: Date.now() });
  const idleRef = useRef(null);
  const startingRef = useRef(false);
  const endedSessionRef = useRef("");
  const routeRef = useRef(emptyRoute());
  const identityRef = useRef({ token, userId: reduxUserId });
  identityRef.current = { token, userId: reduxUserId };
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const reviveRef = useRef(() => {});
  const workflowsRef = useRef(null);
  if (!workflowsRef.current) workflowsRef.current = createWorkflowTracker();

  useEffect(() => {
    sessionRef.current = session;
    setSessionId(token ? session.userSession.Id : null);
    persistSession(session);
  }, [session, token]);

  const sendPageSessions = useCallback(
    async (snapshot, headers) => {
      const toCreate = Object.values(snapshot.pageSession.new);
      const toUpdate = Object.values(snapshot.pageSession.old).filter(isDirty);
      recordDebug({
        session_id: snapshot.userSession.Id,
        part: "batch",
        action: "send",
        detail: { create: toCreate.length, update: toUpdate.length },
      });
      if (!toCreate.length && !toUpdate.length) return;

      let created = null;
      let createFailed = false;
      if (toCreate.length) {
        try {
          const res = await axios.post(dbUrl + "page-session-logs/bulk-create/", toCreate.map(toPayload), withTimeout(headers));
          created = keyByPageSessionKey(res.data);
        } catch {
          createFailed = true;
        }
      }

      let updated = false;
      let updateFailed = false;
      if (toUpdate.length) {
        try {
          await axios.put(dbUrl + "page-session-logs/bulk-update/", toUpdate.map(toPayload), withTimeout(headers));
          updated = true;
        } catch {
          updateFailed = true;
        }
      }

      setSession((prev) => {
        let nextNew = { ...prev.pageSession.new };
        let nextOld = { ...prev.pageSession.old };

        if (created) {
          toCreate.forEach((page) => {
            const key = page.page_session_key;
            const latest = nextNew[key];
            if (!latest) return;
            const merged = { ...(created[key] || {}), ...latest, _attempts: 0, _dirty: latest !== page };
            if (merged.end_time && !merged._dirty) {
              delete nextNew[key];
              return;
            }
            nextOld[key] = merged;
            delete nextNew[key];
          });
        } else if (createFailed) {
          nextNew = bumpAttempts(nextNew, snapshot.pageSession.new);
        }

        if (updated) {
          toUpdate.forEach((page) => {
            const key = page.page_session_key;
            if (nextOld[key] !== page) return;
            if (page.end_time) delete nextOld[key];
            else nextOld[key] = { ...page, _dirty: false, _attempts: 0 };
          });
        } else if (updateFailed) {
          const sent = toUpdate.reduce((acc, page) => ({ ...acc, [page.page_session_key]: true }), {});
          nextOld = bumpAttempts(nextOld, sent);
        }

        return { ...prev, pageSession: { new: nextNew, old: nextOld } };
      });
    },
    [dbUrl]
  );

  const flush = useCallback(async () => {
    if (!token || !dbUrl) return;
    const startedAt = Date.now();
    if (flushingRef.current && startedAt - flushingRef.current < FLUSH_STALE_MS) {
      recordDebug({ session_id: sessionRef.current.userSession.Id, part: "batch", action: "skip", detail: "previous batch still sending" });
      return;
    }
    flushingRef.current = startedAt;
    try {
      await sendPageSessions(sessionRef.current, dbHeaders);
    } finally {
      if (flushingRef.current === startedAt) flushingRef.current = 0;
    }
  }, [token, dbUrl, dbHeaders, sendPageSessions]);

  const updatePageSession = useCallback((key, updater) => {
    if (!key) return;
    setSession((prev) => {
      const bucket = prev.pageSession.new[key] ? "new" : prev.pageSession.old[key] ? "old" : null;
      if (!bucket) return prev;
      return {
        ...prev,
        pageSession: {
          ...prev.pageSession,
          [bucket]: { ...prev.pageSession[bucket], [key]: { ...updater(prev.pageSession[bucket][key]), _dirty: true } },
        },
      };
    });
  }, []);

  const appendEvent = useCallback((key, event) => updatePageSession(key, (page) => appendTo(page, event)), [updatePageSession]);

  const openRoute = useCallback(
    (pathname) => {
      const identity = identityRef.current;
      const person = sessionRef.current.userSession.userId || identity.userId;
      if (!identity.token || !person) return "";

      const { page, ids } = describeRoute(pathname);
      if (isUntracked(page)) return "";
      const idText = ids.join(",");
      const current = routeRef.current;
      const sessionId = sessionRef.current.userSession.Id;

      if (current.key && current.page === page) {
        if (current.ids !== idText) {
          routeRef.current = { ...current, ids: idText };
          recordDebug({ session_id: sessionId, page, part: "route", name: page, action: "context", value: idText });
          updatePageSession(current.key, (record) => {
            const changed = appendTo(record, { type: "route", name: page, action: "context", value: idText, at: now() });
            return { ...changed, details: { ...changed.details, context: { ids } } };
          });
        }
        return current.key;
      }

      const at = now();
      const key = generateId(person);
      const record = {
        person,
        animo_session: sessionId || null,
        page_session_key: key,
        page_name: page,
        start_time: at,
        end_time: null,
        active: true,
        details: { from: current.page || null, context: { ids }, ChannelsViewed: [], events: [] },
      };

      setSession((prev) => {
        const close = (bucket) => (bucket[current.key] ? { ...bucket, [current.key]: closePage(bucket[current.key], at) } : bucket);
        const closedNew = close(prev.pageSession.new);
        if (isPassingThrough(closedNew[current.key])) delete closedNew[current.key];
        return {
          ...prev,
          pageSession: { new: { ...closedNew, [key]: record }, old: close(prev.pageSession.old) },
        };
      });

      if (current.key) {
        recordDebug({ session_id: sessionId, page: current.page, part: "route", name: current.page, action: "leave" });
      }
      recordDebug({
        session_id: sessionId,
        page,
        part: "route",
        name: page,
        action: "open",
        value: idText || undefined,
        detail: current.page ? `from ${current.page}` : undefined,
      });

      routeRef.current = { page, key, ids: idText };
      return key;
    },
    [updatePageSession]
  );

  useEffect(() => {
    workflowsRef.current.closeAreasNotOn(describeRoute(location.pathname).page);
    openRoute(location.pathname);
    reviveRef.current();
  }, [location.pathname, token, reduxUserId, openRoute]);

  useEffect(
    () =>
      subscribe((event) => {
        const { path, ...raw } = event;
        if (isUntracked(pathRef.current) && raw.type !== "tab" && raw.type !== "user") return;
        const named = isComponentType(raw.type) && !raw.component_id ? { ...raw, component_id: namedComponentId(raw.type, raw.name, describeRoute(pathRef.current).page) } : raw;
        const workflows = workflowsRef.current;
        const pageNow = describeRoute(pathRef.current).page;
        workflows.closeAreasNotOn(pageNow);
        const workflowName = named.workflow_name || named.panel;
        const inArea = isAreaType(named.workflow_type);
        let workflowKey = named.workflow_key || workflows.keyOf(workflowName);
        if (!workflowKey && workflowName && inArea) workflowKey = workflows.open(workflowName, named.workflow_type, pageNow);
        const fact = workflowKey && !named.workflow_key ? { ...named, workflow_key: workflowKey } : named;
        if (inArea && named.type === "button" && isFinishWord(named.text || named.name)) workflows.close(workflowName);
        const key = openRoute(pathRef.current);
        reviveRef.current();
        recordDebug({
          at: fact.at,
          session_id: sessionRef.current.userSession.Id,
          page: describeRoute(pathRef.current).page,
          part: fact.type,
          name: fact.name,
          action: fact.action,
          value: fact.value,
          by: fact.by,
          workflow_name: fact.workflow_name || fact.panel,
          workflow_type: fact.workflow_type,
          text: fact.text,
          unnamed: fact.unnamed ? "yes" : undefined,
          form: fact.form ? "yes" : undefined,
          component_id: fact.component_id,
          workflow_key: fact.workflow_key,
          detail: key ? undefined : "not recorded",
        });
        if (key) appendEvent(key, fact);
      }),
    [openRoute, appendEvent]
  );

  useEffect(
    () =>
      watchPanels((action, name, type) => {
        const click = getLastClick();
        const by = click && Date.now() - click.at < CLICK_TO_PANEL_MS ? click.name : undefined;
        const workflows = workflowsRef.current;
        const workflowKey = action === "open" ? workflows.open(name) : workflows.keyOf(name);
        track("panel", name, action, undefined, { by, workflow_key: workflowKey, workflow_name: name, workflow_type: type });
        if (action === "close") workflows.close(name);
      }, (name) => {
        if (isAreaType(workflowsRef.current.typeOf(name))) workflowsRef.current.close(name);
      }),
    []
  );

  useEffect(() => watchClicks(), []);

  useEffect(() => watchResults(), []);

  useEffect(() => watchHighchartsWhenLoaded(), []);

  useEffect(() => {
    const stop = watchIdle({ onActivity: () => reviveRef.current() });
    idleRef.current = stop;
    return stop;
  }, []);

  useEffect(() => {
    const handleVisibility = () => track("tab", null, document.hidden ? "hidden" : "visible");
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      const unreported = idleRef.current && idleRef.current.pending ? idleRef.current.pending() : null;
      const open = unreported ? withEvent(sessionRef.current, routeRef.current.key, unreported) : sessionRef.current;
      const closed = endAllPageSessions(open);
      const toCreate = Object.values(closed.pageSession.new).map(toPayload);
      const toUpdate = Object.values(closed.pageSession.old).filter(isDirty).map(toPayload);
      if (token && dbUrl) {
        if (toCreate.length) sendKeepalive(dbUrl + "page-session-logs/bulk-create/", "POST", toCreate, dbHeaders.headers);
        if (toUpdate.length) sendKeepalive(dbUrl + "page-session-logs/bulk-update/", "PUT", toUpdate, dbHeaders.headers);
      }
      recordDebug({
        session_id: closed.userSession.Id,
        part: "batch",
        action: "send",
        detail: { create: toCreate.length, update: toUpdate.length, reason: "page hidden" },
      });
      const drained = { ...closed, pageSession: emptySession().pageSession };
      sessionRef.current = drained;
      routeRef.current = emptyRoute();
      persistSession(drained);
      setSession((prev) => ({ ...prev, pageSession: emptySession().pageSession }));
    };
    const handlePageShow = (event) => {
      if (event.persisted) openRoute(pathRef.current);
    };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [token, dbUrl, dbHeaders, openRoute]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      const shared = loadStoredUserSession();
      if (shared.Id === sessionRef.current.userSession.Id) return;
      setSession((prev) => ({ ...prev, userSession: shared }));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const startUserSession = useCallback(
    async (userId) => {
      const startTime = now();
      let created = null;
      try {
        const res = await axios.post(
          dbUrl + "animo-sessions/",
          {
            session_key: generateId(userId),
            end_time: null,
            active: true,
            comments: "",
            person: userId,
            frontend_version: FE_VERSION,
            ...getTimeZone(),
          },
          dbHeaders
        );
        created = res.data || null;
      } catch {
      }

      const sessionId = created ? created.id : "";
      const attachSession = (bucket) =>
        Object.keys(bucket).reduce((acc, key) => {
          const page = bucket[key];
          acc[key] = page.animo_session || !sessionId ? page : { ...page, animo_session: sessionId, _dirty: true };
          return acc;
        }, {});

      setSession((prev) => ({
        ...prev,
        userSession: {
          ...prev.userSession,
          ...(created || {}),
          Id: sessionId,
          deviceType: getDeviceType(),
          frontend_version: FE_VERSION,
          ...getTimeZone(),
          tokenTail: tokenTail(token),
          userId,
          start_time: startTime,
          end_time: "",
        },
        pageSession: {
          new: attachSession(prev.pageSession.new),
          old: attachSession(prev.pageSession.old),
        },
      }));

      recordDebug({
        session_id: sessionId,
        part: "session",
        action: "start",
        detail: { person: userId, frontend_version: FE_VERSION, ...getTimeZone() },
      });

      fetchIpAddress().then((ipAddress) => {
        setSession((prev) => ({ ...prev, userSession: { ...prev.userSession, IP: ipAddress } }));
      });
    },
    [dbUrl, dbHeaders, token]
  );

  const ensureUserSession = useCallback(() => {
    if (!token || !reduxUserId || !dbUrl || startingRef.current) return;
    const stored = sessionRef.current.userSession;
    const sameLogin = String(stored.userId) === String(reduxUserId) && stored.tokenTail === tokenTail(token);
    if (stored.Id && sameLogin) return;
    startingRef.current = true;
    startUserSession(reduxUserId).finally(() => {
      startingRef.current = false;
    });
  }, [token, reduxUserId, dbUrl, startUserSession]);

  useEffect(() => {
    ensureUserSession();
  }, [ensureUserSession]);

  tickRef.current = () => {
    ensureUserSession();
    flush();
  };

  const armTimer = useCallback(() => {
    if (beatRef.current.id !== null) clearInterval(beatRef.current.id);
    const id = setInterval(() => {
      beatRef.current.last = Date.now();
      tickRef.current();
    }, FLUSH_INTERVAL_MS);
    beatRef.current = { id, last: Date.now() };
  }, []);

  const reviveTimer = useCallback(() => {
    const stoppedFor = Date.now() - beatRef.current.last;
    if (stoppedFor < FLUSH_INTERVAL_MS * TIMER_LATE_FACTOR) return;
    recordDebug({
      session_id: sessionRef.current.userSession.Id,
      part: "batch",
      action: "revive",
      detail: `timer was stopped for ${Math.round(stoppedFor / 1000)} s`,
    });
    armTimer();
    tickRef.current();
  }, [armTimer]);

  reviveRef.current = reviveTimer;

  useEffect(() => {
    armTimer();
    return () => clearInterval(beatRef.current.id);
  }, [armTimer]);

  const endUserSession = useCallback(() => {
    const closed = endAllPageSessions(sessionRef.current);
    const { Id } = closed.userSession;
    const endTime = now();
    sessionRef.current = closed;
    persistSession(closed);
    routeRef.current = emptyRoute();
    endedSessionRef.current = Id;
    recordDebug({ session_id: Id, part: "session", action: "end", detail: "log out" });

    const finish = async () => {
      await sendPageSessions(closed, dbHeaders);
      if (Id) {
        try {
          await axios.patch(dbUrl + `animo-sessions/${Id}/`, { end_time: endTime, active: false }, dbHeaders);
        } catch {
        }
      }
    };
    finish();

    setSession((prev) => ({ ...prev, userSession: { ...prev.userSession, end_time: endTime } }));
  }, [dbUrl, dbHeaders, sendPageSessions]);

  useEffect(() => {
    const previousToken = previousTokenRef.current;
    previousTokenRef.current = token;
    if (!previousToken || token) return;

    const current = sessionRef.current;
    const { Id } = current.userSession;
    if (Id && endedSessionRef.current !== Id) {
      const headers = getDbHeaders(previousToken);
      const endTime = now();
      recordDebug({ session_id: Id, part: "session", action: "end", detail: "login ended without log out" });
      sendPageSessions(endAllPageSessions(current), headers).then(() =>
        axios.patch(dbUrl + `animo-sessions/${Id}/`, { end_time: endTime, active: false }, headers).catch(() => {})
      );
    }
    routeRef.current = emptyRoute();
    endedSessionRef.current = "";
    setSession(emptySession());
  }, [token, dbUrl, sendPageSessions]);

  const contextValues = useMemo(
    () => ({
      startUserSession,
      endUserSession,
      flush,
    }),
    [startUserSession, endUserSession, flush]
  );

  return <TrackingContext.Provider value={contextValues}>{children}</TrackingContext.Provider>;
};
